<template>
    <section class="litigation-editor">

      <div
        v-if="isCurrentEditor"
        class="editor-owner-strip"
      >
        You are the editor — please save when done.
      </div>
  
      <header class="editor-header">
  
        <div class="editor-heading">
          <h3>
            Edit Litigation
          </h3>
  
          <p>
            Update the litigation information and application questions.
          </p>
        </div>
  
        <div class="editor-action-row">
          <div class="editor-presence">
            <span
              class="editor-avatar"
              :title="editorName"
              :aria-label="`Current editor: ${editorName}`"
            >
              {{ editorInitials }}
            </span>

            <span>
              {{ editorStatus }}
            </span>
          </div>

          <div class="header-actions">
  
          <button
            type="button"
            class="cancel-button"
            :disabled="saving || replacingBanner"
            @click="$emit('cancel')"
          >
            Cancel
          </button>
  
          <button
            type="button"
            class="submit-button"
            :disabled="saving || replacingBanner || !isDirty || !isValid || editingBlocked"
            @click="submitChanges"
          >
            {{ submitButtonLabel }}
          </button>

          </div>
  
        </div>
  
      </header>
  
      <form
        class="editor-form"
        @submit.prevent="submitChanges"
      >
        <fieldset
          class="edit-fields-lock"
          :disabled="editingBlocked"
        >
        <div class="editor-fields">

          <label class="field-group">
            <span>
              Publication Status
            </span>

            <select v-model="publicationStatus">
              <option value="draft">Draft</option>
              <option value="inReview">In Review</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </label>
 
          <label class="field-group">
            <span>
              Internal Litigation Name
            </span>
  
            <input
              v-model="internalName"
              type="text"
            />
          </label>

          <label class="field-group">
            <span>
              Public-Facing Action Name
            </span>

            <input
              v-model="publicActionName"
              type="text"
            />
          </label>

          <div
          v-if="currentImageUrl"
          class="current-banner"
        >
        <img
            :src="currentImageUrl"
            alt="Current litigation banner"
          />
          
          <!-- <span>
            Current banner
          </span> -->
  
        </div>

        <div class="field-group banner-field">
          <label for="litigation-replacement-banner">
            Replace Banner
          </label>

          <div class="banner-input-row">
            <input
              id="litigation-replacement-banner"
              ref="bannerInput"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              :disabled="saving || replacingBanner"
              @change="handleFileSelection"
            />

            <button
              type="button"
              class="replace-banner-button"
              :disabled="!selectedFile || saving || replacingBanner || editingBlocked"
              @click="replaceBannerImage"
            >
              {{ replacingBanner ? "Replacing..." : "Replace Image" }}
            </button>
          </div>

          <span
            v-if="bannerMessage"
            class="banner-message"
            :class="{ error: bannerSaveFailed }"
          >
            {{ bannerMessage }}
          </span>
        </div>
  
          <label class="field-group">
            <span>
              Description
            </span>
  
            <textarea
              v-model="description"
              rows="6"
            />
          </label>

          <LitigationSearchMetadataInput
            v-model="tags"
            :original-values="originalTags"
            label="Tags"
            placeholder="Consumer products or consumer products, infant health"
            hint="User-facing categories."
            add-button-label="Add"
            confirm-new-values
            highlight-new-values
            :suggestions="tagSuggestions"
            :suggestions-loading="tagSuggestionsLoading"
            :suggestions-error="tagSuggestionsError"
          />

          <LitigationSearchMetadataInput
            v-model="audienceTags"
            :original-values="originalAudienceTags"
            label="People and Communities"
            placeholder="Families and caregivers or families and caregivers, veterans"
            hint="Audience groups this Litigation is intended to reach."
            item-name="community"
            add-button-label="Add"
            confirm-new-values
            highlight-new-values
            :suggestions="audienceTagSuggestions"
            :suggestions-loading="audienceTagsLoading"
            :suggestions-error="audienceTagsError"
          />

          <LitigationSearchMetadataInput
            v-model="searchTerms"
            :original-values="originalSearchTerms"
            label="Search Terms"
            placeholder="Company name or company name, product, abbreviation"
            hint="Hidden search aliases."
            item-name="search term"
            add-button-label="Add"
            confirm-new-values
            highlight-new-values
          />
  
          <label class="field-group">
            <span>
              Potential Expectations
            </span>
  
            <textarea
              v-model="potentialExpectations"
              rows="6"
            />
          </label>
  
        </div>
  
        <LitigationQuestionBuilder
          title="Required Qualification Questions"
          description="Questions every applicant must answer."
          :questions="qualificationQuestions"
          @add="addQualificationQuestion"
          @remove="removeQualificationQuestion"
          @move="moveQuestion(qualificationQuestions, $event)"
        />
  
        <LitigationQuestionBuilder
          title="Personal-Injury Questions"
          description="Questions related to personal injuries."
          :questions="personalInjuryQuestions"
          @add="addPersonalInjuryQuestion"
          @remove="removePersonalInjuryQuestion"
          @move="moveQuestion(personalInjuryQuestions, $event)"
        />
  
        <LitigationQuestionBuilder
          title="Property or Economic-Damage Questions"
          description="Questions related to property or financial damages."
          :questions="economicDamageQuestions"
          @add="addEconomicDamageQuestion"
          @remove="removeEconomicDamageQuestion"
          @move="moveQuestion(economicDamageQuestions, $event)"
        />

        </fieldset>

        <p
          v-if="editingBlocked"
          class="editing-message"
        >
          {{ leaseError || `${editorName} is currently editing this litigation. This view is read-only.` }}
        </p>
  
        <p
          v-if="errorMessage"
          class="error-message"
        >
          {{ errorMessage }}
        </p>
  
      </form>
  
    </section>
  </template>
  
  <script>
  import {
    computed,
    onMounted,
    ref
  } from "vue";
  
  import LitigationQuestionBuilder from "@/components/litigations/LitigationQuestionBuilder.vue";
  import LitigationSearchMetadataInput from "@/components/litigations/LitigationSearchMetadataInput.vue";
  import { useLitigationEditLease } from "@/composables/useLitigationEditLease";
  
  import {
    fetchNotificationTagCatalog,
    replaceLitigationBannerFile,
    updateLitigation
  } from "@/firebase/firestore";
  
  import {
    buildLitigationQuestions,
    cleanLitigationQuestionDrafts,
    createLitigationQuestionDraft,
    litigationQuestionDrafts
  } from "@/models/litigationQuestions";
  import {
    normalizeSearchMetadata
  } from "@/models/searchMetadata";
  import { normalizeNotificationTagIds } from "@/models/notificationTags";
  
  export default {
    name:
      "LitigationEditor",
  
    components: {
      LitigationQuestionBuilder,
      LitigationSearchMetadataInput
    },
  
    props: {
      litigation: {
        type:
          Object,
        required:
          true
      },
      adminId: {
        type: String,
        default: ""
      },
      adminName: {
        type: String,
        default: ""
      }
    },
  
    emits: [
      "cancel",
      "saved"
    ],
  
    setup(
      props,
      {
        emit
      }
    ) {

      const litigationId =
        computed(() =>
          props.litigation.litigationId ??
          props.litigation.id ??
          ""
        );

      const {
        editingBlocked,
        editorInitials,
        editorName,
        editorStatus,
        isCurrentEditor,
        leaseError
      } = useLitigationEditLease({
        litigationId,
        adminId: computed(() => props.adminId),
        adminName: computed(() => props.adminName)
      });
  
      const internalName =
        ref(
          props.litigation.internalName ?? ""
        );

      const publicActionName =
        ref(
          props.litigation.publicActionName ?? ""
        );
  
      const description =
        ref(
          props.litigation.description ?? ""
        );

      const originalTags =
        normalizeSearchMetadata(
          props.litigation.tags
        );

      const originalSearchTerms =
        normalizeSearchMetadata(
          props.litigation.searchTerms
        );

      const tags =
        ref([...originalTags]);

      const originalAudienceTags = ref(
        normalizeSearchMetadata(props.litigation.audienceTags)
      );
      const audienceTags = ref([...originalAudienceTags.value]);

      const tagSuggestions = ref([]);
      const tagSuggestionsLoading = ref(true);
      const tagSuggestionsError = ref("");
      const audienceTagSuggestions = ref([]);
      const audienceTagsLoading = ref(true);
      const audienceTagsError = ref("");

      const loadNotificationCatalog = async () => {
        tagSuggestionsLoading.value = true;
        tagSuggestionsError.value = "";
        audienceTagsLoading.value = true;
        audienceTagsError.value = "";

        try {
          const catalog = await fetchNotificationTagCatalog();
          tagSuggestions.value = catalog
            .filter(tag => tag.category === "legal_topic" && tag.active)
            .map(tag => tag.label);
          const audienceCatalog = catalog.filter(
            tag => tag.category === "audience"
          );
          audienceTagSuggestions.value = audienceCatalog
            .filter(tag => tag.active)
            .map(tag => tag.label);

          if (
            audienceTags.value.length === 0 &&
            normalizeNotificationTagIds(props.litigation.audienceTagIds).length > 0
          ) {
            const selectedIds = new Set(
              normalizeNotificationTagIds(props.litigation.audienceTagIds)
            );
            const selectedLabels = audienceCatalog
              .filter(tag => selectedIds.has(tag.id))
              .map(tag => tag.label);
            originalAudienceTags.value = selectedLabels;
            audienceTags.value = [...selectedLabels];
            originalFormSnapshot.value = formSnapshot.value;
          }
        } catch (error) {
          console.error("Unable to load notification tag suggestions:", error);
          tagSuggestionsError.value = "Saved tag suggestions are unavailable.";
          audienceTagsError.value = "People and Communities are unavailable.";
        } finally {
          tagSuggestionsLoading.value = false;
          audienceTagsLoading.value = false;
        }
      };

      onMounted(loadNotificationCatalog);

      const searchTerms =
        ref([...originalSearchTerms]);
  
      const potentialExpectations =
        ref(
          props.litigation.potentialExpectations ?? ""
        );
  
      const publicationStatus =
        ref(
          props.litigation.publicationStatus ?? "draft"
        );
  
      const qualificationQuestions =
        ref(
          litigationQuestionDrafts(
            props.litigation.questions,
            "qualification"
          )
        );
  
      const personalInjuryQuestions =
        ref(
          litigationQuestionDrafts(
            props.litigation.questions,
            "personalInjury"
          )
        );
  
      const economicDamageQuestions =
        ref(
          litigationQuestionDrafts(
            props.litigation.questions,
            "economicDamage"
          )
        );
  
      const currentImageUrl =
        ref(
          props.litigation.imageUrl ?? ""
        );
  
      const selectedFile =
        ref(null);

      const bannerInput =
        ref(null);

      const replacingBanner =
        ref(false);

      const bannerMessage =
        ref("");

      const bannerSaveFailed =
        ref(false);
  
      const saving =
        ref(false);
  
      const errorMessage =
        ref("");

      const formSnapshot =
        computed(() => JSON.stringify({
          internalName:
            internalName.value,
          publicActionName:
            publicActionName.value,
          description:
            description.value,
          tags:
            tags.value,
          audienceTags:
            audienceTags.value,
          searchTerms:
            searchTerms.value,
          potentialExpectations:
            potentialExpectations.value,
          publicationStatus:
            publicationStatus.value,
          qualificationQuestions:
            qualificationQuestions.value,
          personalInjuryQuestions:
            personalInjuryQuestions.value,
          economicDamageQuestions:
            economicDamageQuestions.value,
          selectedFile:
            selectedFile.value ? {
              name:
                selectedFile.value.name,
              size:
                selectedFile.value.size,
              lastModified:
                selectedFile.value.lastModified
            } :
              null
        }));

      const originalFormSnapshot =
        ref(formSnapshot.value);

      const isDirty =
        computed(() =>
          formSnapshot.value !== originalFormSnapshot.value
        );
  
      const isValid =
        computed(() => {
  
          return (
            internalName.value.trim() !== "" &&
            publicActionName.value.trim() !== "" &&
            description.value.trim() !== "" &&
            cleanLitigationQuestionDrafts(
              qualificationQuestions.value
            ).length > 0
          );
        });

      const submitButtonLabel =
        computed(() => {

          const isInReview =
            publicationStatus.value === "inReview";

          if (saving.value) {
            return isInReview ? "Saving..." : "Publishing...";
          }

          return isInReview ? "Save Changes" : "Publish Changes";
        });
  
      const addQualificationQuestion =
        () => {
  
          qualificationQuestions.value.push(
            createLitigationQuestionDraft()
          );
        };
  
      const addPersonalInjuryQuestion =
        () => {
  
          personalInjuryQuestions.value.push(
            createLitigationQuestionDraft()
          );
        };
  
      const addEconomicDamageQuestion =
        () => {
  
          economicDamageQuestions.value.push(
            createLitigationQuestionDraft()
          );
        };
  
      const removeQualificationQuestion =
        index => {
  
          qualificationQuestions.value.splice(
            index,
            1
          );
  
          if (
            qualificationQuestions.value.length === 0
          ) {
  
            qualificationQuestions.value.push(
              createLitigationQuestionDraft()
            );
          }
        };
  
      const removePersonalInjuryQuestion =
        index => {
  
          personalInjuryQuestions.value.splice(
            index,
            1
          );
        };
  
      const removeEconomicDamageQuestion =
        index => {
  
          economicDamageQuestions.value.splice(
            index,
            1
          );
        };

      const moveQuestion =
        (
          questions,
          { index, direction }
        ) => {

          const destination =
            index + direction;

          if (
            destination < 0 ||
            destination >= questions.length
          ) {
            return;
          }

          const [question] =
            questions.splice(index, 1);

          questions.splice(
            destination,
            0,
            question
          );
        };
  
      const handleFileSelection =
        event => {
  
          selectedFile.value =
            event.target.files?.[0] ??
            null;

          bannerMessage.value =
            "";

          bannerSaveFailed.value =
            false;
        };

      const persistBannerReplacement =
        async () => {

          const file =
            selectedFile.value;

          if (!file) {
            return false;
          }

          const bannerUrl =
            await replaceLitigationBannerFile(
              litigationId.value,
              file
            );

          currentImageUrl.value =
            bannerUrl;

          selectedFile.value =
            null;

          if (bannerInput.value) {
            bannerInput.value.value =
              "";
          }

          return true;
        };

      const replaceBannerImage =
        async () => {

          if (
            !selectedFile.value ||
            saving.value ||
            replacingBanner.value ||
            editingBlocked.value
          ) {
            return;
          }

          replacingBanner.value =
            true;

          bannerMessage.value =
            "";

          bannerSaveFailed.value =
            false;

          try {
            await persistBannerReplacement();

            bannerMessage.value =
              "Banner image replaced.";
          } catch (error) {
            console.error(
              "Unable to replace Litigation banner:",
              error
            );

            bannerSaveFailed.value =
              true;

            bannerMessage.value =
              error?.code === "admin/storage-preflight-failed"
                ? error.message
                : "Unable to replace the banner image.";
          } finally {
            replacingBanner.value =
              false;
          }
        };
  
      const submitChanges =
        async () => {
  
          if (
            !isValid.value ||
            !isDirty.value ||
            saving.value ||
            replacingBanner.value ||
            editingBlocked.value
          ) {
  
            return;
          }
  
          saving.value =
            true;
  
          errorMessage.value =
            "";
  
          try {
  
            const updated =
              await updateLitigation(
                litigationId.value,
                {
                  internalName:
                    internalName.value.trim(),

                  publicActionName:
                    publicActionName.value.trim(),
  
                  description:
                    description.value.trim(),

                  tags:
                    normalizeSearchMetadata(
                      tags.value
                    ),

                  audienceTags:
                    normalizeSearchMetadata(
                      audienceTags.value
                    ),

                  searchTerms:
                    normalizeSearchMetadata(
                      searchTerms.value
                    ),
  
                  potentialExpectations:
                    potentialExpectations.value.trim() ||
                    null,

                  questions:
                    buildLitigationQuestions({
                      qualificationQuestions:
                        qualificationQuestions.value,
                      personalInjuryQuestions:
                        personalInjuryQuestions.value,
                      economicDamageQuestions:
                        economicDamageQuestions.value
                    }),

                  publicationStatus:
                    publicationStatus.value
                }
              );
  
            if (!updated) {
  
              throw new Error(
                "Litigation was not updated."
              );
            }
  
            if (
              selectedFile.value
            ) {
              await persistBannerReplacement();
            }
  
            emit(
              "saved"
            );
  
          } catch (error) {
  
            console.error(
              "❌ Unable to update litigation:",
              error
            );
  
            errorMessage.value =
              "Unable to save the litigation changes.";
  
          } finally {
  
            saving.value =
              false;
          }
        };
  
      return {
        internalName,
        publicActionName,
        description,
        tags,
        tagSuggestions,
        tagSuggestionsLoading,
        tagSuggestionsError,
        audienceTags,
        originalAudienceTags,
        audienceTagSuggestions,
        audienceTagsLoading,
        audienceTagsError,
        searchTerms,
        originalTags,
        originalSearchTerms,
        potentialExpectations,
        publicationStatus,
        qualificationQuestions,
        personalInjuryQuestions,
        economicDamageQuestions,
        currentImageUrl,
        selectedFile,
        bannerInput,
        replacingBanner,
        bannerMessage,
        bannerSaveFailed,
        saving,
        errorMessage,
        editingBlocked,
        editorInitials,
        editorName,
        editorStatus,
        isCurrentEditor,
        leaseError,
        isDirty,
        isValid,
        submitButtonLabel,
        addQualificationQuestion,
        addPersonalInjuryQuestion,
        addEconomicDamageQuestion,
        removeQualificationQuestion,
        removePersonalInjuryQuestion,
        removeEconomicDamageQuestion,
        moveQuestion,
        handleFileSelection,
        replaceBannerImage,
        submitChanges
      };
    }
  };
  
  </script>

<style scoped>
.litigation-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #fbfcfb;
}

.editor-owner-strip {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 16px;
  flex-shrink: 0;
  background: #18ab69;
  color: #ffffff;
  font-size: 0.86rem;
  font-weight: 700;
  text-align: center;
}

/* Sticky header */

.editor-header {
  position: sticky;
  top: 0;
  z-index: 10;

  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 24px;

  padding: 18px 22px;
  border-bottom: 1px solid #e2e7e4;
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(8px);
}

.editor-heading,
.editor-action-row {
  width: 100%;
}

.editor-action-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.editor-presence {
  display: flex;
  align-items: center;
  gap: 9px;
  color: #65716b;
  font-size: 0.88rem;
  font-weight: 600;
}

.editor-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #eaf6ef;
  color: #128052;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.editor-header h3 {
  margin: 0;
  font-size: 1.25rem;
}

.editor-header p {
  margin: 5px 0 0;
  color: #747e79;
}

.header-actions {
  display: flex;
  flex-shrink: 0;
  gap: 12px;
}

/* Scrollable content */

.editor-form {
  display: flex;
  flex-direction: column;
  gap: 24px;

  padding: 24px;
  overflow-y: auto;
}

.edit-fields-lock {
  display: contents;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.editing-message {
  margin: 0;
  padding: 12px 14px;
  border: 1px solid #efd9a8;
  border-radius: 9px;
  background: #fffaf0;
  color: #8a6721;
}

.editor-fields {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Main fields */

.field-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  color: #28342e;
  font-weight: 600;
}

.field-group input,
.field-group textarea,
.field-group select {
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 13px 14px;
  border: 1px solid #d7ded9;
  border-radius: 10px;
  background: #ffffff;
  color: #1f2a25;
  font: inherit;
}

.field-group textarea {
  min-height: 150px;
  resize: vertical;
  line-height: 1.5;
}


.field-group input:focus,
.field-group textarea:focus,
.field-group select:focus {
  border-color: #18ab69;
  outline: 3px solid rgba(24, 171, 105, 0.12);
}

/* Live setting */

.live-setting {
  display: flex;
  align-items: flex-start;
  gap: 14px;

  width: 100%;
  padding: 18px;
  box-sizing: border-box;

  border: 1px solid #dfe5e1;
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
}

.live-setting input {
  width: 20px;
  height: 20px;
  margin: 2px 0 0;
  flex-shrink: 0;
  accent-color: #18ab69;
}

.live-setting-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.live-setting-title {
  color: #24312b;
  font-weight: 700;
}

.live-setting-description {
  color: #747e79;
  font-size: 0.88rem;
  font-weight: 400;
}

/* Header buttons */

.cancel-button,
.submit-button {
  min-width: 135px;
  padding: 11px 18px;
  border: 0;
  border-radius: 9px;
  color: #ffffff;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color 0.18s ease,
    opacity 0.18s ease;
}

.cancel-button {
  background: #d64545;
}

.cancel-button:hover:not(:disabled) {
  background: #be3838;
}

.submit-button {
  background: #18ab69;
}

.submit-button:hover:not(:disabled) {
  background: #15945c;
}

.cancel-button:disabled,
.submit-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

/* Banner */

.banner-field {
  padding: 18px;
  border: 1px solid #e2e7e4;
  border-radius: 12px;
  background: #ffffff;
}

.banner-field > label {
  color: #28342e;
  font-weight: 600;
}

.banner-input-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.banner-input-row input {
  min-width: 0;
  flex: 1;
}

.replace-banner-button {
  min-height: 44px;
  flex-shrink: 0;
  padding: 10px 16px;
  border: 1px solid #18ab69;
  border-radius: 9px;
  background: #18ab69;
  color: #ffffff;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.replace-banner-button:hover:not(:disabled) {
  border-color: #15945c;
  background: #15945c;
}

.replace-banner-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.banner-message {
  color: #178554;
  font-size: 0.84rem;
  font-weight: 500;
}

.banner-message.error {
  color: #a33636;
}

.current-banner {
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: #28342e;
  font-weight: 600;
}

.current-banner img {
  display: block;
  width: 100%;
  max-width: 720px;
  max-height: 260px;
  object-fit: cover;
  border: 1px solid #dfe5e1;
  border-radius: 12px;
}

/* Error */

.error-message {
  margin: 0;
  padding: 12px 14px;
  border: 1px solid #efc5c5;
  border-radius: 9px;
  background: #fff7f7;
  color: #a33636;
}

/* Responsive */

@media (max-width: 700px) {
  .editor-header {
    align-items: flex-start;
  }

  .editor-action-row {
    align-items: stretch;
    flex-direction: column;
  }

  .header-actions {
    width: 100%;
  }

  .cancel-button,
  .submit-button {
    flex: 1;
    min-width: 0;
  }

  .editor-form {
    padding: 18px;
  }

  .banner-input-row {
    align-items: stretch;
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .header-actions {
    flex-direction: column-reverse;
  }

  .cancel-button,
  .submit-button {
    width: 100%;
  }
}
</style>
